import { z } from 'zod'

// API Response Schema - Updated to match new API format
export const sentryEventApiSchema = z
  .object({
    id: z.string(),
    shortId: z.string().optional(),
    title: z.string(),
    culprit: z.string().nullable().optional(), // Transaction/path where error occurred, can be null
    status: z.enum(['unresolved', 'resolved', 'ignored']).optional(),
    substatus: z.string().optional(), // e.g., "escalating", "ongoing"
    level: z.enum(['error', 'warning', 'info', 'fatal']).optional(),
    priority: z.union([z.enum(['high', 'medium', 'low']), z.string()]).optional(), // Allow string as fallback
    project: z
      .object({
        id: z.string().optional(),
        name: z.string(),
        slug: z.string().optional(),
        platform: z.string().optional(),
      })
      .passthrough(), // Allow additional project fields
    metadata: z
      .object({
        title: z.string().nullable().optional(), // Error title for handled errors, can be null
        value: z.string().nullable().optional(),
        type: z.string().nullable().optional(),
        filename: z.string().nullable().optional(),
        function: z.string().nullable().optional(),
        in_app_frame_mix: z.string().nullable().optional(),
        sdk: z
          .object({
            name: z.string().optional(),
            name_normalized: z.string().optional(),
          })
          .passthrough()
          .optional(),
      })
      .passthrough()
      .optional(), // Use passthrough to allow additional metadata fields
    // Additional fields that might be present
    environment: z.string().optional(),
    latestEnvironment: z.string().optional(), // Latest environment from API response
    lastSeen: z.union([z.string(), z.date()]).optional(),
    firstSeen: z.union([z.string(), z.date()]).optional(),
    count: z.union([z.string(), z.number()]).optional(), // Event count as string or number
    userCount: z.number().optional(),
    isUnhandled: z.boolean().optional(), // Direct field from API
    logger: z.string().nullable().optional(), // Logger name (e.g., "javascript", "python"), can be null
    permalink: z.string().optional(),
    platform: z.string().optional(),
    type: z.string().optional(),
    issueType: z.string().optional(),
    issueCategory: z.string().optional(),
    lifetime: z
      .object({
        count: z.union([z.string(), z.number()]).optional(),
        userCount: z.number().optional(),
        firstSeen: z.union([z.string(), z.date()]).optional(),
        lastSeen: z.union([z.string(), z.date()]).optional(),
        stats: z.record(z.string(), z.union([z.number(), z.string(), z.null()])).optional(),
      })
      .optional(),
    filtered: z
      .object({
        count: z.union([z.string(), z.number()]).optional(),
        userCount: z.number().optional(),
        firstSeen: z.union([z.string(), z.date()]).optional(),
        lastSeen: z.union([z.string(), z.date()]).optional(),
        stats: z.record(z.string(), z.union([z.number(), z.string(), z.null()])).optional(),
      })
      .optional(),
    stats: z.record(z.string(), z.union([z.number(), z.string(), z.null()])).optional(), // 24h stats array
    inbox: z.record(z.string(), z.unknown()).nullable().optional(), // Inbox status
    owners: z.array(z.record(z.string(), z.unknown())).nullable().optional(), // Issue owners
  })
  .passthrough() // Allow additional fields in the response

export const sentryEventsApiResponseSchema = z
  .object({
    success: z.boolean().optional(), // Make success optional as some endpoints may not include it
    data: z.array(sentryEventApiSchema).optional(), // Make data optional - some endpoints may not include it
    status: z.number().optional(),
    requestUrl: z.string().optional(),
    recordCount: z.number().optional(),
    totalCount: z.number().optional(), // API also returns totalCount at top level
    totalCountDuration: z.number().optional(), // API also returns totalCountDuration
    workstream: z.string().optional(), // Some endpoints return workstream at top level
    pagination: z
      .object({
        nextCursor: z.string().nullable().optional(),
        hasMore: z.boolean().optional(),
      })
      .optional(),
    timestamp: z.union([z.string(), z.date()]).optional(),
    'x-hits': z.number().nullable().optional(), // Total count from API response headers (can be null)
    filteredCount: z.number().optional(), // Filtered count from API response
    categoryCounts: z
      .object({
        total: z.number().optional(),
        linehaul: z
          .object({
            count: z.number(),
            percentage: z.string().optional(),
          })
          .optional(),
        inbound: z
          .object({
            count: z.number(),
            percentage: z.string().optional(),
          })
          .optional(),
        outbound: z
          .object({
            count: z.number(),
            percentage: z.string().optional(),
          })
          .optional(),
        dispatch: z
          .object({
            count: z.number(),
            percentage: z.string().optional(),
          })
          .optional(),
        backoffice: z
          .object({
            count: z.number(),
            percentage: z.string().optional(),
          })
          .optional(),
        other: z
          .object({
            count: z.number(),
            percentage: z.string().optional(),
          })
          .optional(),
      })
      .optional(),
  })
  .passthrough() // Allow additional fields

export type SentryEventApi = z.infer<typeof sentryEventApiSchema>
export type SentryEventsApiResponse = z.infer<typeof sentryEventsApiResponseSchema>

// Priority Counts API Response Schema
export const priorityCountsApiResponseSchema = z
  .object({
    success: z.boolean(),
    priorityCounts: z.object({
      high: z.number(),
      medium: z.number(),
      low: z.number(),
    }),
    totalIssues: z.number(),
    pagesFetched: z.number().optional(),
    parallelBatches: z.number().optional(),
    issuesPerPage: z.number().optional(),
    parallelPages: z.number().optional(),
    executionTime: z.string().optional(),
    'x-hits': z.number().nullable().optional(), // Can be null when no results
    requestUrl: z.string().optional(),
    filters: z
      .object({
        query: z.string().nullable().optional(), // Can be null when no workstream filter
        project: z.array(z.string()).optional(),
        environment: z.string().nullable().optional(),
        maxIssues: z.number().optional(),
        parallelPages: z.number().optional(),
        includeTrend: z.boolean().optional(),
        start: z.union([z.string(), z.date()]).optional(), // Accept both string and Date
        end: z.union([z.string(), z.date()]).optional(), // Accept both string and Date
        statsPeriod: z.string().optional(), // Add statsPeriod field
      })
      .optional(),
    timestamp: z.union([z.string(), z.date()]).optional(), // Accept both string and Date (httpCommon auto-converts)
  })
  .passthrough()

export type PriorityCountsApiResponse = z.infer<typeof priorityCountsApiResponseSchema>

// Severity Percentage API Response Schema
// Use z.coerce.number() so API responses that send numbers as strings (e.g. "-84.6") still parse
const severityComparisonEntrySchema = z.object({
  count: z.coerce.number(),
  previousCount: z.coerce.number(),
  change: z.coerce.number(),
  percentageChange: z.coerce.number(),
})

export const severityPercentageApiResponseSchema = z
  .object({
    success: z.boolean().optional(),
    'Severity-counts-Comparison': z
      .object({
        high: severityComparisonEntrySchema.optional(),
        medium: severityComparisonEntrySchema.optional(),
        low: severityComparisonEntrySchema.optional(),
        total: severityComparisonEntrySchema.optional(),
        comparisonPeriod: z.string().optional(),
      })
      .optional(),
    executionTime: z.string().optional(),
    filters: z
      .object({
        query: z.string().optional(),
        project: z.array(z.string()).optional(),
        environment: z.string().nullable().optional(),
        maxIssues: z.number().optional(),
        start: z.union([z.string(), z.date()]).optional(),
        end: z.union([z.string(), z.date()]).optional(),
      })
      .optional(),
    timestamp: z.union([z.string(), z.date()]).optional(),
    cached: z.boolean().optional(),
  })
  .passthrough()

export type SeverityPercentageApiResponse = z.infer<typeof severityPercentageApiResponseSchema>

// Trend Graph API Response Schema
export const trendGraphApiResponseSchema = z
  .object({
    success: z.boolean(),
    data: z.array(
      z.object({
        date: z.union([z.string(), z.date()]), // Accept both string and Date (httpCommon auto-converts)
        count: z.number(),
      })
    ),
    filters: z
      .object({
        statsPeriod: z.string().optional(),
        severity: z.string().optional(),
        environment: z.string().nullable().optional(), // Can be null when no environment is selected
        project: z.array(z.string()).optional(),
        interval: z.string().optional(),
      })
      .optional(),
    cached: z.boolean().optional(),
    executionTime: z.string().optional(),
    timestamp: z.union([z.string(), z.date()]).optional(),
  })
  .passthrough()

export type TrendGraphApiResponse = z.infer<typeof trendGraphApiResponseSchema>

// UI Model Schema (transformed from API)
export const sentryIssueSchema = z.object({
  id: z.string(),
  title: z.string(),
  subtitle: z.string().optional(),
  type: z.string(),
  status: z.enum(['unresolved', 'resolved', 'ignored']),
  level: z.enum(['error', 'warning', 'info', 'fatal']),
  project: z.string(),
  enrichedEnvironment: z.string().optional(),
  lastSeen: z.string(),
  lastSeenTimestamp: z.union([z.date(), z.string()]).optional(), // Original timestamp for formatting
  age: z.string(),
  trend: z.enum(['ongoing', 'escalating', 'resolved', 'new']),
  trendValue: z.number().optional(),
  events24h: z.number(),
  events14d: z.number(),
  totalEvents: z.number(),
  affectedUsers: z.number(),
  priority: z.enum(['high', 'medium', 'low']).optional(),
  assignee: z.string().optional(),
  path: z.string().optional(),
  transaction: z.string().optional(),
  workstream: z.string().optional(),
  tags: z.array(z.string()).optional(),
  isUnhandled: z.boolean().optional(),
  logger: z.string().optional(), // Logger name (e.g., "javascript", "python")
  shortId: z.string().optional(), // Short ID for display
})

export type SentryIssue = z.infer<typeof sentryIssueSchema>
